import isEmpty from 'lodash/isEmpty';
import { Rectangle, ModifierUtils, Logger } from 'flux-core';
import { RichText } from './../../../../framework/easeljs/rich-text';
import { ICursor, ICursorDrawable } from './../../../interaction/cursor-drawable.i';
import { InteractionSubContext } from './../../../interaction/cursor-drawable.i';
import { ITextView } from 'flux-diagram-composer';
import { Shape } from '@creately/createjs-module';
import { DEFUALT_TEXT_STYLES, IRectangle, IText, ITextContent } from 'flux-definition';
import * as Handlebars from 'handlebars';

/**
 * This is the text view that will be used for rendering texts on the canvas
 * and it extends the easlj Text class.
 * This contains the common functionalities for rendering
 * the texts contanied in any shape type i.e. Connector or Shape texts.
 *
 * The easljs Text class has some limitations.
 * Tt does not support HTML text, and can only display one font style at a time.
 * To use multiple font styles, multiple text instances should be created and position manually.
 * Also it supports only some of the text related styles.
 *
 * Supported styles / features
 *
 *   1. "font" CSS property https://developer.mozilla.org/en-US/docs/Web/CSS/font
 *
 *        font-style - italic, oblique, normal etc.
 *        font-variant - small-caps, petite-caps etc.
 *        font-weight - bold, bolder, lighter, normal , 300 etc.
 *        font-size/line-height - 12px, 1em , 12pt  etc.
 *        font-family
 *        e.g. this.font = "bold italic large serif"
 *
 *   2. Horizontal text alignment. Any of "start", "end", "left", "right", and "center"
 *        e.g. this.textAlign = "left";
 *
 *   3. Word wrap to multiple lines by setting a fixed width to the text
 *        e.g. this.lineWidth = 300;
 *
 *   4. Text color
 *        e.g. this.color = '#89455';
 *
 *   5. Line height - Indicates the line height (vertical distance between baselines) for multi-line text.
 *        e.g. this.lineHeight = 5;
 *
 *
 * Following styles / features will be implemented and are not avalible for the moment
 *
 *   1. Text with Multiple styles
 *
 *   2. Underline
 *
 *   3. Strikethourgh
 *
 *   4. Superscript / Subscript
 *
 *   5. Ordered and unordered lists
 *
 *   6. Character spacing
 *
 *   7. Text transform  - uppercase , lowercase , capitalize etc.
 *
 * @author thisun
 * @since 2018-01-17
 */
export class TextView extends RichText implements ITextView, ICursorDrawable {
    /**
     * Number of pixels to add as padding when caching the text view.
     */
    private static CACHE_PADDING = 10;

    /**
     * The pixel ratio of the cached image.
     */
    private static CACHE_DENSITY = 2;

    /**
     * The width and height of the cached bitmap image of the shape.
     */
    private cachedBounds: { width: number, height: number } = null;

    constructor( model: IText ) {
        super( DEFUALT_TEXT_STYLES );
        this.name = model.id;
        this.hitArea = new Shape();
        this.width = ( model as any ).width;
        this.height = ( model as any ).height;
        this.update( model );
    }

    /**
     * Returns the cursor and tail defined for the given context.
     * The return value may be null if no cursor is availbale for the given context
     */
    public getCursor( contexts: InteractionSubContext[]): ICursor {
        if (
            contexts.includes( 'editor' ) &&
            contexts.includes( 'normal' ) &&
            contexts.includes( 'selected' )
        ) {
            return { cursor: 'text', tail: null, type: 'css' };
        }
        return { cursor: 'default', tail: null, type: 'css', viewingArea: 'app' };
    }

    /**
     * Create a rectangle shape the same size as the text
     * and assign it as the hitArea
     */
    public updateHitArea( model: IText, rect: IRectangle ) {
        const bounds = Rectangle.from( rect );
        const hitArea = this.hitArea as Shape;
        hitArea.graphics.clear();
        hitArea.graphics.beginFill( '#000' );
        if ( model.hitArea ) {
            hitArea.graphics.drawRect(
                // Note: After enabling partial text alignments, the width of the text view
                // is equal to hit area width so x, y can be 0 always.
                0,
                0,
                model.hitArea.width,
                model.hitArea.height,
            );
        } else if ( model.editable ) {
            hitArea.graphics.drawRect( bounds.x, bounds.y, this.lineWidth, bounds.height );
        }
        hitArea.graphics.endFill();
    }

    /**
     * Cache the shape as a bitmap and draw the bitmap on the diagram.
     * When the model bound changes, the cache has to be fully reset.
     */
    public updateViewCache( model: IText, change?: any ): void {
        if ( this.cachedBounds ) {
            if ( this.cachedBounds.width === this.lineWidth && this.cachedBounds.height === model.height ) {
                if ( ModifierUtils.hasChanges( change, `texts.${model.id}.content` )
                 || ModifierUtils.hasChanges( change, `texts.${model.id}.handlebars` )) {
                    this.updateCache();
                }
            } else {
                this.uncache();
                this.cachedBounds = null;
            }
        }
        if ( !this.cachedBounds ) {
            if ( !this.hasRenderableText( model.content )) {
                return;
            }
            // FIXME: this is a temporary fix until the swimlane shape and all other shapes where text gets added
            //        dynamically is fixed. The text model width/height is not set when text is added by these shapes.
            //        The `if` condition below is necessary and by doing this we will lose some optimizations.
            if ( !this.lineWidth || !model.height ) {
                Logger.warning( 'TextView: Found text model without width/height', model );
                return;
            }
            this.cache(
                -TextView.CACHE_PADDING,
                -TextView.CACHE_PADDING,
                this.lineWidth + 2 * TextView.CACHE_PADDING,
                model.height + 2 * TextView.CACHE_PADDING,
                TextView.CACHE_DENSITY,
            );
            this.cachedBounds = { width: this.lineWidth, height: model.height };
        }
    }

    /**
     * This function updates the text viiew as the text model changes
     * @param text The text model get data to create image and to position
     */
    public update( model: IText, change?: any ) {
        if ( this.hasRenderableText( model.content )) {
            if ( !isEmpty(( model as any ).handlebars )) {
                const data = JSON.parse(( model as any ).handlebars );
                const modified = model.content.map( run => {
                    // Square brackets [] Allow for spaces when replacing
                    let newText = run.text.replace( /\{\{/g, '{{{[' );
                    newText = newText.replace( /\}\}/g, ']}}}' );
                    const source = newText;
                    let result = source;
                    try {
                        const template = Handlebars.compile( source );
                        result = template( data );
                    } catch ( e ) {
                    }
                    return { ...run, text: result };
                });
                this.text = modified;
            } else {
                this.text = model.content;
            }
            this.lineWidth = model.width;
        } else if ( this.hasRenderableText( model.placeholder )) {
            this.text = model.placeholder;
            this.lineWidth = model.width;
        } else {
            this.text = [];
            this.lineWidth = 0;
        }
    }

    /**
     * Destroys the text view for disposal.
     */
    public destroy() {
        if ( !this.parent ) {
            Logger.error( 'TextView.destroy called but this.parent was null.' );
            return;
        }
        this.parent.removeChild( this );
    }

    /**
     * Returns the bounds of the text
     * @param data: Object[]    Carota text objects array
     * @param width: number    Optional param to wrap the text
     * FIXME -  The default width should be infinite and here it is just a big number
     */
    public getTextBounds( data: Object[] | string, width: number = 10000 ) {
        const canvas: HTMLCanvasElement = document.createElement( 'canvas' ) as any;
        const rt =  new RichText( DEFUALT_TEXT_STYLES );
        rt.text = data;
        rt.lineWidth = width;
        rt.draw( canvas.getContext( '2d' ));
        return rt.getBounds();
    }

    /**
     * Check whether the model has text which should be rendered
     */
    private hasRenderableText( content: ITextContent[]): boolean {
        if ( content && content.length ) {
            for ( const word of content ) {
                // NOTE: This function is to check whether the model has
                // text which can be rendered, so there are cases where
                // the model will not have text, so it is necessary to
                // check whether the context has text.
                if ( word && word.text &&
                    word.text.trim().length > 0 ) {
                    return true;
                }
            }
        }
        return false;
    }

}
